import { supabase } from "./supabase";

// ---------- Shops ----------
export async function getShopBySlug(slug) {
  const { data, error } = await supabase
    .from("shops")
    .select("*")
    .eq("slug", slug)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function createShop({ slug, name, tagline, admin_pin }) {
  // Pre-check slug
  const existing = await supabase
    .from("shops")
    .select("id")
    .eq("slug", slug)
    .maybeSingle();
  if (existing.data) throw new Error("That subdomain is already taken.");

  const { data, error } = await supabase
    .from("shops")
    .insert({ slug, name, tagline, admin_pin, is_open: true })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function listAllShops() {
  const { data, error } = await supabase
    .from("shops")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function deleteShop(shopId) {
  const { error } = await supabase.from("shops").delete().eq("id", shopId);
  if (error) throw error;
}

export function isShopSuspended(shop) {
  if (!shop) return false;
  if (shop.blocked === true) return { reason: "blocked" };
  if (shop.active === false) return { reason: "inactive" };
  if (shop.payment_status === "expired") return { reason: "expired" };
  if (shop.expiry_date) {
    const exp = new Date(shop.expiry_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (exp < today) return { reason: "expired" };
  }
  return false;
}

export async function updateShopOpen(shopId, isOpen) {
  const { error } = await supabase
    .from("shops")
    .update({ is_open: isOpen })
    .eq("id", shopId);
  if (error) throw error;
}

export async function updateShop(shopId, patch) {
  const { error } = await supabase.from("shops").update(patch).eq("id", shopId);
  if (error) throw error;
}

export async function uploadShopLogo(shopId, file) {
  const ext = (file.name.split(".").pop() || "png").toLowerCase();
  const path = `${shopId}/logo-${Date.now()}.${ext}`;
  const { error: upErr } = await supabase.storage
    .from("shop-logos")
    .upload(path, file, { upsert: true, contentType: file.type });
  if (upErr) throw upErr;
  const { data } = supabase.storage.from("shop-logos").getPublicUrl(path);
  return data.publicUrl;
}

export async function notifyWhatsAppViaShop(shop, token) {
  if (!shop?.wa_enabled || !shop?.wa_webhook_url) {
    throw new Error("WhatsApp is not configured for this shop.");
  }
  const payload = {
    provider: shop.wa_provider || "generic",
    to: token.whatsapp,
    token: token.token_number,
    customer: token.customer_name,
    service: token.service_name,
    shop: shop.name,
    shop_slug: shop.slug,
    message: `Hello ${token.customer_name}, your token #${token.token_number} at ${shop.name} is coming up. Please head over now.`,
  };
  const res = await fetch(shop.wa_webhook_url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    mode: "cors",
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Webhook ${res.status}: ${text.slice(0, 200)}`);
  }
  return true;
}

// ---------- Services ----------
export async function listServices(shopId, { activeOnly = false } = {}) {
  let q = supabase
    .from("services")
    .select("*")
    .eq("shop_id", shopId)
    .order("sort_order", { ascending: true });
  if (activeOnly) q = q.eq("is_active", true);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function createService(shopId, payload) {
  const { data, error } = await supabase
    .from("services")
    .insert({ ...payload, shop_id: shopId })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function bulkCreateServices(shopId, services) {
  if (!services?.length) return [];
  const rows = services.map((s) => ({ ...s, shop_id: shopId }));
  const { data, error } = await supabase.from("services").insert(rows).select();
  if (error) throw error;
  return data;
}

export async function updateService(id, patch) {
  const { error } = await supabase.from("services").update(patch).eq("id", id);
  if (error) throw error;
}

export async function deleteService(id) {
  const { error } = await supabase.from("services").delete().eq("id", id);
  if (error) throw error;
}

// ---------- Tokens ----------
function startOfDayISO() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export async function listTokensForShop(shopId) {
  const { data, error } = await supabase
    .from("tokens")
    .select("*")
    .eq("shop_id", shopId)
    .gte("created_at", startOfDayISO())
    .order("token_number", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function listTokenHistory(shopId, { days = 30 } = {}) {
  const since = new Date();
  since.setDate(since.getDate() - days);
  since.setHours(0, 0, 0, 0);
  const { data, error } = await supabase
    .from("tokens")
    .select("*")
    .eq("shop_id", shopId)
    .gte("created_at", since.toISOString())
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getTokenByNumber(shopId, tokenNumber) {
  const { data, error } = await supabase
    .from("tokens")
    .select("*")
    .eq("shop_id", shopId)
    .eq("token_number", Number(tokenNumber))
    .gte("created_at", startOfDayISO())
    .maybeSingle();
  if (error) throw error;
  return data;
}

// Looks up TODAY's active (waiting/serving) token for a given phone number.
// Used by the "Already booked today?" lookup on the booking page.
// Normalises both sides to digits-only so "+91 98765 43210" matches "9876543210".
export async function findActiveTokenByPhone(shopId, phoneRaw) {
  const digits = String(phoneRaw || "").replace(/\D/g, "");
  if (!digits) return null;
  const tail = digits.slice(-10); // last 10 digits — robust to country-code variations
  const { data, error } = await supabase
    .from("tokens")
    .select("*")
    .eq("shop_id", shopId)
    .in("status", ["waiting", "serving"])
    .gte("created_at", startOfDayISO())
    .order("created_at", { ascending: false });
  if (error) throw error;
  const match = (data || []).find((t) => {
    const tDigits = String(t.whatsapp || "").replace(/\D/g, "");
    return tDigits.slice(-10) === tail;
  });
  return match || null;
}

export async function createToken(shopId, payload) {
  const { data: nextNum, error: rpcErr } = await supabase.rpc(
    "next_token_number",
    { p_shop_id: shopId }
  );
  if (rpcErr) throw rpcErr;
  const { data, error } = await supabase
    .from("tokens")
    .insert({
      shop_id: shopId,
      token_number: nextNum,
      customer_name: payload.customer_name,
      whatsapp: payload.whatsapp,
      avatar: payload.avatar,
      service_id: payload.service_id,
      service_name: payload.service_name,
      status: "waiting",
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateTokenStatus(id, status, extra = {}) {
  const patch = { status, ...extra };
  if (status === "serving") patch.serving_started_at = new Date().toISOString();
  if (status === "completed") patch.completed_at = new Date().toISOString();
  const { error } = await supabase.from("tokens").update(patch).eq("id", id);
  if (error) throw error;
}

// ---------- Realtime subscriptions ----------
export function subscribeShopTokens(shopId, onChange) {
  const channel = supabase
    .channel(`tokens-${shopId}-${Math.random()}`)
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "tokens", filter: `shop_id=eq.${shopId}` },
      (payload) => onChange(payload)
    )
    .subscribe();
  return () => supabase.removeChannel(channel);
}

export function subscribeShop(shopId, onChange) {
  const channel = supabase
    .channel(`shop-${shopId}-${Math.random()}`)
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "shops", filter: `id=eq.${shopId}` },
      (payload) => onChange(payload)
    )
    .subscribe();
  return () => supabase.removeChannel(channel);
}

export function subscribeServices(shopId, onChange) {
  const channel = supabase
    .channel(`services-${shopId}-${Math.random()}`)
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "services", filter: `shop_id=eq.${shopId}` },
      (payload) => onChange(payload)
    )
    .subscribe();
  return () => supabase.removeChannel(channel);
}
